import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { useRef } from 'react';

import SymbolSelector from '@flipster/features/src/history-page/shared/symbol-selector';
import { DatePicker } from '@flipster/features/src/shared/date-picker';
import { FormattedMessage } from '@flipster/i18n';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Chip, createSingleChoice } from '@flipster/react/design-system/chip';
import CalendarDaysIcon from '@flipster/react/icon/generated/CalendarDaysIcon';
import { flex, stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { HistoryType, tradeHistoryBunja } from './state';
import TradeHistoryTable, { TradeHistoryTableRef } from './trade-history-table';

const Choice = createSingleChoice<HistoryType>();

const loggingMap = {
  position: 'Position' as const,
  order: 'Order' as const,
  fill: 'Fill' as const,
};

const TradeHistory = () => {
  const { tabAtom, dateRangeAtom, isCheckedCurrentMarketAtom, searchSymbolAtom, currentSymbol } =
    useBunja(tradeHistoryBunja);

  const tableRef = useRef<TradeHistoryTableRef>(null);

  const [tab, setTab] = useAtom(tabAtom);
  const [dateRange, setDateRange] = useAtom(dateRangeAtom);
  const [searchSymbol, setSearchSymbol] = useAtom(searchSymbolAtom);
  const [isCurrentMarket, setIsCurrentMarket] = useAtom(isCheckedCurrentMarketAtom);

  return (
    <div className={stack({ height: '100%' })}>
      <div
        className={flex({
          justify: 'space-between',
          align: 'center',
          wrap: 'wrap',
          gap: 12,
          w: '100%',
          flexShrink: 0,
          pt: 8,
          pb: 16,
          position: 'sticky',
          top: 0,
          zIndex: 3,
          bg: 'background.default.level-0',
        })}
      >
        <div className={flex({ align: 'center' })}>
          <Choice
            value={tab}
            onChange={(tab) => {
              setTab(tab);
              tableRef?.current?.scrollTop();
              segment.track(PositionEvent.PositionOrderStatusTabClick, {
                tab_type: 'History',
                execution_stage: loggingMap[tab],
              });
            }}
          >
            <Choice.Chip value='position' label={<FormattedMessage id='Autogen_common.r251126.s_Position' />} />
            <Choice.Chip value='order' label={<FormattedMessage id='Trade.r250401.Order' />} />
            <Choice.Chip value='fill' label={<FormattedMessage id='Trade.r260127.Web_trade_history_fill' />} />
          </Choice>
        </div>
        <div className={flex({ align: 'center' })}>
          <Checkbox
            css={{ mr: 16 }}
            checked={isCurrentMarket}
            onChange={() => {
              segment.track(TradeEvent.OrderFormHistoryCurrentMarketClick, {
                toggle_on: !isCurrentMarket,
                execute_stage: loggingMap[tab],
                symbol: currentSymbol,
              });
              setIsCurrentMarket((isChecked) => !isChecked);
            }}
          >
            <FormattedMessage id='Trade.r260127.Web_trade_history_current_market_filter' />
          </Checkbox>
          <DatePicker.Root placement='bottom-end'>
            <DatePicker.Trigger asChild>
              <Chip.Choice
                onClick={() => {
                  segment.track(TradeEvent.OrderFormHistoryCalendarClick, { symbol: currentSymbol });
                }}
                icon={<CalendarDaysIcon />}
                selected={Boolean(dateRange)}
                css={{ mr: 8 }}
              />
            </DatePicker.Trigger>
            <DatePicker.RangePicker
              selected={dateRange}
              onChange={setDateRange}
              confirmText={<FormattedMessage id='Common.r250923.Search' />}
              portal
            />
          </DatePicker.Root>
          <SymbolSelector
            shape='rounded'
            symbol={searchSymbol}
            disabled={isCurrentMarket}
            onSelect={(symbol) => setSearchSymbol(symbol)}
            portal
          />
        </div>
      </div>
      <TradeHistoryTable ref={tableRef} tab={tab} />
    </div>
  );
};

export default TradeHistory;
